import { useEffect, useMemo, useRef, useState } from "react";
import { motion as Motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import cls from "./Loading.module.scss";

// ✅ Desktop (wide) portal images
import loadingImg1 from "@/assets/UI/loadingImg1.webp";
import loadingImg2 from "@/assets/UI/loadingImg2.webp";
import loadingImg3 from "@/assets/UI/loadingImg3.webp";
import loadingImg4 from "@/assets/UI/loadingImg4.webp";

// ✅ Mobile (tall) portal images
import loadingImgM1 from "@/assets/UI/loadingImgM1.webp";
import loadingImgM2 from "@/assets/UI/loadingImgM2.webp";
import loadingImgM3 from "@/assets/UI/loadingImgM3.webp";
import loadingImgM4 from "@/assets/UI/loadingImgM4.webp";

/* ----------------------- Image pools ----------------------- */
const DESKTOP_IMAGES = [loadingImg1, loadingImg2, loadingImg3, loadingImg4];
const MOBILE_IMAGES = [loadingImgM1, loadingImgM2, loadingImgM3, loadingImgM4];

/* ----------------------- Helpers ----------------------- */
const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

const pickRandomDifferent = (arr, current) => {
  if (!arr?.length) return current || null;
  if (arr.length === 1) return arr[0];

  let next = current;
  let guard = 0;
  while (next === current && guard < 12) {
    next = pickRandom(arr);
    guard += 1;
  }
  return next;
};

const isMobileView = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 768px)").matches;
};

const safeGetSession = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetSession = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

export default function Loading({
  fullscreen = true,
  titleKey = "loading.title",
  tipsKeyPrefix = "loading.tips",
  showTips = true,
  cycleMs = 1500,

  // ✅ visual controls
  showVisual = true,

  // rotation: interval-based
  rotateImages = false,
  rotateMs = 6000,

  // ✅ Optional upgrades (enabled by props)
  lockPerSession = true, // locks chosen image per session (separately for mobile/desktop)
  rotateMsDesktop = null, // if set, overrides rotateMs on desktop
  rotateMsMobile = null, // if set, overrides rotateMs on mobile
  syncBgToTips = false, // if true, background changes when tip changes
  syncEveryNTips = 1, // change bg every N tips (1 = every tip, 2 = every 2 tips)
}) {
  const { t } = useTranslation();

  /* ------------------------------- Tips logic ------------------------------- */
  const tips = useMemo(() => {
    const list = [];
    for (let i = 0; i < 20; i += 1) {
      const key = `${tipsKeyPrefix}.${i}`;
      const value = t(key, { defaultValue: "" });
      if (!value) break;
      list.push(value);
    }
    return list.length ? list : [t("loading.fallbackTip")];
  }, [t, tipsKeyPrefix]);

  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    if (!showTips || !tips?.length) return;

    const id = window.setInterval(
      () => {
        setTipIndex((p) => (p + 1) % tips.length);
      },
      Math.max(900, Number(cycleMs) || 1500),
    );

    return () => window.clearInterval(id);
  }, [tips, showTips, cycleMs]);

  /* ----------------------- Mobile/Desktop detection ----------------------- */
  const [isMobile, setIsMobile] = useState(() => isMobileView());

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);

    // initial sync
    setIsMobile(mq.matches);

    // modern + fallback
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else window.addEventListener("resize", onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else window.removeEventListener("resize", onChange);
    };
  }, []);

  const imagePool = useMemo(
    () => (isMobile ? MOBILE_IMAGES : DESKTOP_IMAGES),
    [isMobile],
  );

  /* ----------------------- Session lock logic ----------------------- */
  const sessionKey = useMemo(
    () => (isMobile ? "noty_loading_bg_mobile" : "noty_loading_bg_desktop"),
    [isMobile],
  );

  const pickInitialImage = () => {
    if (!imagePool.length) return null;

    if (lockPerSession) {
      const saved = safeGetSession(sessionKey);
      if (saved && imagePool.includes(saved)) return saved;

      const chosen = pickRandom(imagePool);
      safeSetSession(sessionKey, chosen);
      return chosen;
    }

    return pickRandom(imagePool);
  };

  const [portalImg, setPortalImg] = useState(() => pickInitialImage());

  // When switching mobile/desktop, choose a correct image from that pool (respect session lock)
  useEffect(() => {
    if (!showVisual) return;
    setPortalImg(pickInitialImage());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, showVisual]);

  /* ----------------------- Rotation (interval) ----------------------- */
  useEffect(() => {
    if (!showVisual) return;
    if (!rotateImages) return;
    if (!imagePool.length) return;

    const base = Number(rotateMs) || 6000;
    const ms =
      isMobile && rotateMsMobile != null
        ? Number(rotateMsMobile)
        : !isMobile && rotateMsDesktop != null
          ? Number(rotateMsDesktop)
          : base;

    const safeMs = Math.max(1200, ms || 6000);

    const id = window.setInterval(() => {
      setPortalImg((cur) => {
        const next = pickRandomDifferent(imagePool, cur);
        if (lockPerSession) safeSetSession(sessionKey, next);
        return next;
      });
    }, safeMs);

    return () => window.clearInterval(id);
  }, [
    showVisual,
    rotateImages,
    rotateMs,
    rotateMsMobile,
    rotateMsDesktop,
    isMobile,
    imagePool,
    lockPerSession,
    sessionKey,
  ]);

  /* ----------------------- Sync BG to tips (optional) ----------------------- */
  const lastTipSyncRef = useRef(-1);

  useEffect(() => {
    if (!showVisual) return;
    if (!syncBgToTips) return;
    if (!showTips) return;
    if (!imagePool.length) return;

    // only every N tips
    if (syncEveryNTips > 1) {
      if (tipIndex % syncEveryNTips !== 0) return;
    }

    // avoid duplicate runs for same index (StrictMode safety)
    if (lastTipSyncRef.current === tipIndex) return;
    lastTipSyncRef.current = tipIndex;

    setPortalImg((cur) => {
      const next = pickRandomDifferent(imagePool, cur);
      if (lockPerSession) safeSetSession(sessionKey, next);
      return next;
    });
  }, [
    tipIndex,
    showVisual,
    syncBgToTips,
    syncEveryNTips,
    showTips,
    imagePool,
    lockPerSession,
    sessionKey,
  ]);

  /* ----------------------- Background scale differences ----------------------- */
  const bgScale = isMobile ? 1.005 : 1.02; // mobile: calmer, desktop: slightly more depth

  return (
    <div
      className={fullscreen ? cls.fullscreen : cls.inline}
      style={
        showVisual && portalImg ? { "--bg": `url(${portalImg})` } : undefined
      }
    >
      {/* ✅ Fullscreen background image (crossfade + scale per device) */}
      {showVisual && portalImg && (
        <AnimatePresence mode="wait" initial={false}>
          <Motion.div
            key={portalImg}
            className={cls.bgImage}
            style={{ backgroundImage: `url(${portalImg})` }}
            initial={{ opacity: 0, scale: bgScale }}
            animate={{ opacity: 1, scale: bgScale }}
            exit={{ opacity: 0, scale: bgScale }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            aria-hidden="true"
          />
        </AnimatePresence>
      )}

      {/* overlays */}
      <div className={cls.bgVignette} aria-hidden="true" />
      <div className={cls.bgColorWash} aria-hidden="true" />
      <div className={cls.bgParticles} aria-hidden="true" />

      {/* foreground */}
      <Motion.div
        className={cls.card3d}
        initial={{ opacity: 0, y: 14, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        role="status"
        aria-live="polite"
      >
        <div className={cls.cardInner}>
          <div className={cls.brandLine}>
            <span className={cls.brandBadge}>{t("loading.badge")}</span>
            <span className={cls.brandName}>{t("loading.brand")}</span>
          </div>

          <div className={cls.chatLine}>
            <span className={cls.chatTitle}>{t(titleKey)}</span>
            <TypingDots />
          </div>

          {/* Tip bubble replaces subtitle + no separate tipsWrap */}
          {showTips && (
            <div className={cls.tipsBubble}>
              <AnimatePresence mode="wait" initial={false}>
                <Motion.div
                  key={tipIndex}
                  className={cls.tipText}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.28, ease: "easeOut" }}
                >
                  {tips[tipIndex]}
                </Motion.div>
              </AnimatePresence>
            </div>
          )}

          <div className={cls.progressWrap} aria-hidden="true">
            <div className={cls.progressTrack}>
              <div className={cls.progressBar} />
              <div className={cls.progressGlow} />
            </div>
          </div>
        </div>

        <div className={cls.cardRim} aria-hidden="true" />
      </Motion.div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className={cls.dots} aria-hidden="true">
      <span className={cls.dot} />
      <span className={cls.dot} />
      <span className={cls.dot} />
    </span>
  );
}
